import { customInstance } from "@repo/api-client";

/**
 * Client contract for the venue-site authoring surface (`/api/v1/venues/{venueId}/site`, §C.11). Goes
 * through `@repo/api-client`'s fetch mutator (bearer token + typed `ApiError`), NOT a raw fetch — a 409
 * stale write and a 422 (SM-1 asset / validation) come back as `ApiError` carrying the RFC 9457 problem.
 * Thin accessors matching the landed `scheduleApi`/`floorApi` idiom; the Orval client is regenerated from
 * the backend OpenAPI at serial integration (`just gen`).
 */

export type CtaState = "ENABLED" | "DISABLED_INDEFINITELY" | "PAUSED_UNTIL";
export type ResolvedCta = "BOOKABLE" | "CONTACT_ONLY";

export interface MenuItemResponse {
  name: string;
  description: string | null;
  priceMinor: number | null;
  currency: string | null;
}
export interface MenuSectionResponse {
  name: string;
  items: MenuItemResponse[];
}
export interface MenuResponse {
  mode: "DOCUMENTS" | "STRUCTURED";
  assetIds: string[] | null;
  sections: MenuSectionResponse[] | null;
}
export interface ContactResponse {
  phone: string | null;
  email: string | null;
  website: string | null;
}
export interface SocialLinkResponse {
  platform: string;
  url: string;
}
export interface SiteContentResponse {
  description: string | null;
  cuisineTypes: string[];
  priceRange: number | null;
  menu: MenuResponse | null;
  galleryOrder: string[];
  contact: ContactResponse;
  socialLinks: SocialLinkResponse[];
}
export interface BrandingResponse {
  logoAssetId: string | null;
  primaryColor: string | null;
  defaultBranding: boolean;
}
export interface BookingCtaResponse {
  state: CtaState;
  pausedUntil: string | null;
  resolved: ResolvedCta;
}
export interface SiteResponse {
  venueId: string;
  draft: SiteContentResponse;
  published: SiteContentResponse | null;
  branding: BrandingResponse;
  bookingCta: BookingCtaResponse;
  offline: boolean;
  /** The "hidden from search" toggle (venue-seo, R2). false = the venue is excluded from search. */
  seoIndexable: boolean;
  contentVersion: number;
  hasUnpublishedChanges: boolean;
  version: number;
}

/** The PATCH body: the whole editable draft + branding + the CAS version. */
export interface SiteDraftRequest {
  version: number;
  description?: string | null;
  cuisineTypes?: string[];
  priceRange?: number | null;
  menu?: {
    mode: "DOCUMENTS" | "STRUCTURED";
    assetIds?: string[] | null;
    sections?: Array<{
      name: string;
      items: Array<{
        name: string;
        description?: string | null;
        priceMinor?: number | null;
        currency?: string | null;
      }>;
    }> | null;
  } | null;
  galleryOrder?: string[];
  contact?: {
    phone?: string | null;
    country?: string | null;
    email?: string | null;
    website?: string | null;
  } | null;
  socialLinks?: SocialLinkResponse[];
  branding?: { logoAssetId?: string | null; primaryColor?: string | null } | null;
}

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/site`;

const jsonHeaders = { "Content-Type": "application/json" } as const;

export const siteApi = {
  get: (venueId: string) => customInstance<SiteResponse>(base(venueId), { method: "GET" }),

  saveDraft: (venueId: string, body: SiteDraftRequest) =>
    customInstance<SiteResponse>(base(venueId), {
      method: "PATCH",
      headers: jsonHeaders,
      body: JSON.stringify(body),
    }),

  publish: (venueId: string, version: number) =>
    customInstance<SiteResponse>(`${base(venueId)}/publish`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ version }),
    }),

  preview: (venueId: string) =>
    customInstance<{ previewUrl: string }>(`${base(venueId)}/preview`, {
      method: "POST",
      headers: jsonHeaders,
      body: "{}",
    }),

  setBookingCta: (
    venueId: string,
    body: { state: CtaState; pausedUntil?: string | null; version: number },
  ) =>
    customInstance<SiteResponse>(`${base(venueId)}/booking-cta`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify(body),
    }),

  setOffline: (venueId: string, offline: boolean, version: number) =>
    customInstance<SiteResponse>(`${base(venueId)}/offline`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify({ offline, version }),
    }),

  setSeoIndexable: (venueId: string, seoIndexable: boolean, version: number) =>
    customInstance<SiteResponse>(`${base(venueId)}/seo`, {
      method: "PATCH",
      headers: jsonHeaders,
      body: JSON.stringify({ seoIndexable, version }),
    }),
};

/** The `["venue", venueId, "site"]` query-key family this feature owns (eventQueryKeyMap convention). */
export const siteKeys = {
  site: (venueId: string) => ["venue", venueId, "site"] as const,
};
